import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {JNodeInfos, JNodeProps, JNodeShortProps, URLTREE} from "/@lib@/store/urlTree.js";
import {IInfo, IInfoBroker, IInfoProducer} from "/@lib@/commons/infos.js";
import {JUserSpecifiedRolesMap} from "/@lib@/commons/roles.js";
import {IDepotEnv, IDepotResUiEnv, IDepotShortResEnv, IDepotShortResUiEnv, IDepotUiEnv} from "/@lib@/store/depot.js";
import {DatasetMultiLevels, IArea, IControlLabelContext} from "/@lib@/commons/areas.js";
import {IResAreaAdapter, ResChildrenArea, ResFrameArea, ResHoverArea, ResIdentFieldSetArea, RESINFO, ResInfoArea, ResMainArea, ResTabsArea} from "/@back@/store/areas/resViewAreas.js";
import {IView} from "/@lib@/commons/views.js";
import {JDepotCidResult} from "/@lib@/store/cid.js";
import {JInteractionDatas} from "/@lib@/core/cid.js";
import {SEC} from "/@lib@/commons/security.js";
import {IField} from "/@lib@/commons/forms.js";
import {FILES} from "/@lib@/commons/io/files.js";
import {wspCd} from "/@lib@/wsp/wsp.js";
import {srcUriSub} from "/@lib@/wsp/item.js";


/**
 * Path d'une ressource de dépot dans l'urlTree.
 * "" : racine non versionnée
 * "/a" : path vers le noeud /a
 * "/a?b" : path vers le noeud /a?b
 * "@xx" : racine versionnée
 * "@xx/a" : path vers le noeud /a
 * "@xx/a?b" : path vers le noeud /a?b
 */
export type resPath = string


/** Message diffusé par un resViewer pour signifier que la res est actuellement la res courante. */
export class InfoCurrentRes implements IInfo {
	/** null pour signifier aucun item courant. */
	resPath: resPath;

	/** props de la res courante si resPath non null. */
	nodeProps?: JNodeShortProps;

	constructor(resPath: resPath | null, nodeProps?: JNodeShortProps) {
		this.resPath = resPath;
		this.nodeProps = nodeProps;
	}

	infoHolders?: IInfoProducer[];
}

/** Message requête pour demander la res actuellement courante. */
export class InfoReqCurrentRes implements IInfo {
	/** null pour signifier aucune res courante. */
	resPath?: resPath;
	/** props de la res courante si resPath non null.*/
	resProps?: JNodeProps;

	infoHolders?: IInfoProducer[];
}

/** Message demandant le focus sur une nouvelle res courante. */
export class InfoFocusRes implements IInfo {
	/** null pour demander l'effacement de la res courante. */
	resPath?: resPath;

	/** La view qui a capté et traité le focus informe les consumers suivants et l'appelant en se référençant ici. */
	focusHandled?: IView;

	infoHolders?: IInfoProducer[];

	constructor(resPath: resPath) {
		this.resPath = resPath;
	}
}

/** Message notifiant une modification en cours de traitement sur une ressource. */
export class InfoUpdatePendingRes implements IInfo {
	/** Path non versionné */
	resPath: resPath;
	/** */
	done: Promise<JDepotCidResult & Pick<JPersistMetas, 'resId' | 'path'>>;

	infoHolders?: IInfoProducer[];

	constructor(resPath: resPath, done: Promise<JDepotCidResult & Pick<JPersistMetas, 'resId' | 'path'>>) {
		this.resPath = resPath;
		this.done = done;
	}
}


/**
 * ActionContext pour les actions relatives à une liste de ressources d'un depot.
 */
export interface IDepotCtx {

	reg: IReg<IDepotUiEnv>

	resList: JNodeShortProps[];

	/**
	 * Peut être une surcharge ou une simple ref à reg.env.infoBroker.
	 * Si undefined/null le contexte appelant souhaite explicitement bloquer le dispatch, ne pas rechercher reg.env.infoBroker.
	 */
	infoBroker?: IInfoBroker

	/** Ui d'appel, idéalement focusable pour re-sélection après dialogue. */
	emitter: HTMLElement
}

/**
 * ActionContext pour les actions relatives à une ressource d'un depot dans un contexte partiel de cette res.
 */
export interface IDepotShortResCtx extends IRegPointer<IDepotShortResUiEnv> {

	reg: IReg<IDepotShortResUiEnv>

	/**
	 * Surcharge de l'infoBroker du reg.
	 * usage : ctx.infoBroker || env.reg.env.infoBroker
	 * Instancier un InfoBroker vide pour bloquer explicitement le dispatch
	 */
	infoBroker?: IInfoBroker

	/** Ui d'appel, idéalement focusable pour re-sélection après dialogue. */
	emitter: HTMLElement
}

/**
 * ActionContext pour les actions relatives à une ressource d'un depot dans un contexte complet.
 */
export interface IDepotResCtx extends IRegPointer<IDepotResUiEnv> {

	reg: IReg<IDepotResUiEnv>

	/**
	 * Surcharge de l'infoBroker du reg.
	 * usage : ctx.infoBroker || env.reg.env.infoBroker
	 * Instancier un InfoBroker vide pour bloquer explicitement le dispatch
	 */
	infoBroker?: IInfoBroker

	/** Ui d'appel, idéalement focusable pour re-sélection après dialogue. */
	emitter?: HTMLElement
}


/** Keys des persistMetas généralement utilisées. */
export interface JPersistMetas {
	path?: string
	'Content-type'?: string
	processing?: string
	resId?: string
	olderResId?: string
	movedFrom?: string
	deployedBy?: string
	updatedBy?: string
	deployDate?: number
	updateDate?: number
	originalName?: string
	scChainOrigin?: { serverUrl: { credentials: 'omit' | 'same-origin' | 'include', url: string }, wspCode: wspCd, refUri: srcUriSub }
	userRolesMap?: JUserSpecifiedRolesMap
	state?: 'new' | 'ready' | 'failure'
	trashed?: boolean
	unlisted?: boolean
	lc?: string

	[key: string]: any
}

export type EPersistMeta = keyof JPersistMetas

/** Directives CID et PersistMetas */
export interface JCidMetas extends JPersistMetas {
	olderPath?: string
	olderTs?: number
	action?: 'removeAll' | 'removeNode'
	abortSession?: true
}

/** Paramètres des interactions CID en plus des directives CID et PersistMetas */
export interface JCidInteractionMetas extends JCidMetas, Partial<JInteractionDatas> {
	/** [IN,OUT] Alternative au path, traduit en path à la volée en param IN. */
	publicUrl?: string
}

/**
 *
 */
export class DepotResTypeProv {

	public nullType: IResType;
	public unknownType: IResType;
	public defaultFolderType: IResType;
	public movedType: IResType;

	get dataSets(): Map<string, DatasetMultiLevels> {
		return this._dataSets || (this._dataSets || new Map());
	}

	protected _dataSets: Map<string, DatasetMultiLevels>;

	protected prcMap: Map<string, IResType> = new Map();

	protected resTypesTree: (IResType | IDirResType)[];


	get defaultResFileType(): IResType | null {
		return this.prcMap.get("file");
	}

	getResTypeFor(res: JNodeShortProps | null): IResType {
		if (res == null) return this.nullType;
		if (res.prc == null) {
			if (res.t === "noContent") return this.defaultFolderType;
			if (res.t === "moved") return this.movedType;
			return this.unknownType;
		}
		return this.prcMap.get(res.prc) || this.unknownType;
	}

	getResTypeByPrc(prc: string | null): IResType {
		if (prc == null) return this.nullType;
		return this.prcMap.get(prc) || this.unknownType;
	}

	getResTypesTree(filter?: (resType: IResType) => boolean): (IDirResType | IResType)[] {
		if (!filter) return this.resTypesTree;

		function copy(tree: (IDirResType | IResType)[]) {
			const result: (IDirResType | IResType)[] = [];
			for (let entry of tree) {
				if (isDirResType(entry)) {
					const ch = copy(entry.children);
					if (ch.length > 0) {
						const newEntry = Object.create(entry);
						entry.children = ch;
						result.push(newEntry);
					}
				} else if (filter(entry)) {
					result.push(entry);
				}
			}
			return result;
		}

		return copy(this.resTypesTree);
	}

	/** Retourne les extensions autorisées. Ex: ".png", ".xml". */
	getExtensions(): string[] {
		const allExts = new Set<string>;
		this.getResTypesTree((resType) => !resType.prcIsNoContent && !resType.prcNoCreator).forEach(entry => (entry as IResType).prcExts?.forEach(allExts.add, allExts));
		return Array.from(allExts).sort();
	}

	/**
	 * Temporaire (devs java en attente) : utilisé pour bloquer toute modif d'identité si le depot contient au moins un VCB.
	 */
	hasAnyVCB() {
		if (this._hasAnyVCB === undefined) {
			for (let itemType of this.prcMap.values()) {
				if (itemType.prcVersionning === "VCB") {
					this._hasAnyVCB = true;
					return true;
				}
			}
			this._hasAnyVCB = false;
		}
		return this._hasAnyVCB;
	}

	protected _hasAnyVCB: boolean;

	humanPath(resPath: resPath): string {
		return resPath || "Accueil";
	}

	addPrcResType(resType: IResType): IResType {
		this.prcMap.set(resType.prc, resType);
		return resType;
	}

	setPrcResTypes(...resTypes: IResType[]): this {
		for (const r of resTypes) this.prcMap.set(r.prc, r);
		return this;
	}

	setDefaultFolderType(resType: IResType): IResType {
		this.defaultFolderType = resType;
		if (resType.prc) this.prcMap.set(resType.prc, resType);
		return resType;
	}

	setUnknownType(resType: IResType): IResType {
		this.unknownType = resType;
		return resType;
	}

	setNullType(resType: IResType): IResType {
		this.nullType = resType;
		return resType;
	}

	setMovedType(resType: IResType): IResType {
		this.movedType = resType;
		return resType;
	}

	/** Arbre personnalisé pour l'affichage des ResTypes. */
	setResTypesTree(tree: (IResType | IDirResType)[]): this {
		this.resTypesTree = tree;
		return this;
	}


	/** A appeler en fin d'initialisation pour compléter les valeurs par défaut. */
	initDefaults(reg: IReg<IDepotEnv>) {
		if (!this.nullType) this.nullType = new ResType(reg, "#null").override("prcLabel", "Contenu inexistant").setIcon("/@skin@/store/objects/res/null.svg");
		if (!this.unknownType) this.unknownType = new ResType(reg, "#unknown").override("prcLabel", "Autre contenu").setIcon("/@skin@/store/objects/res/default.svg");
		if (!this.defaultFolderType) {
			this.setDefaultFolderType(new ResType(reg, "folder").override("prcLabel", "Dossier").setIcon("/@skin@/store/objects/res/folder.svg").setIconOpened("/@skin@/store/objects/res/folderOpened.svg")
				.addView("main", RESVIEW_main_FOLDER)
			);
		}
		if (!this.movedType) {
			this.movedType = new ResType(reg, "#moved").override("prcLabel", "Contenu déplacé").setIcon("/@skin@/store/objects/res/moved.svg")
				.addView("infosMain", RESVIEW_infosMain_MOVED);
		}
		if (!this.resTypesTree) this.resTypesTree = Array.from(this.prcMap.values());
	}

	visStateLabel(res: JNodeShortProps): string {
		if (res.trashed) return "Invisible";
		if (res.unlisted) return "Limité";
		return "Visible";
	}

	visStateDescription(res: JNodeShortProps): string {
		if (res.trashed) return "Aucun accès en ligne";
		if (res.unlisted) return "Masqué, uniquement accessible par son URL";
		return "Normalement affiché et accessible";
	}

	visStateIconUrl(res: JNodeShortProps): string {
		if (res.trashed) return "/@skin@/store/actions/visResInvisible.svg";
		if (res.unlisted) return "/@skin@/store/actions/visResLimited.svg";
		return "/@skin@/store/actions/visResVisible.svg";
	}

	visStateMenuLabel = "Visibilité en ligne";
}

/**
 * Type de ressource correspondant généralement à un processing.
 */
export interface IResType {

	/** Registre pour ce type de ressource. */
	readonly reg: IReg<IDepotResTypeEnv>

	/** Code du processing */
	readonly prc: string

	/** Nom du processing */
	readonly prcLabel: string

	/** Icone du processing. */
	readonly prcIcon: string

	/** Icone du processing en état ouvert si ce processing peut être vu sous forme de dossier. */
	readonly prcIconOpened?: string

	/** true si le processing est de type "folder" */
	readonly prcIsFolder: boolean

	/** true si le processing est sans contenu */
	readonly prcIsNoContent: boolean

	/** true si le processing doit être masqué des écans de création */
	readonly prcNoCreator: boolean

	/** Extensions associées à cette ressource */
	readonly prcExts?: string[]

	/** Modalité de versionning de cette ressource. */
	readonly prcVersionning?: 'VCS' | 'VCB' | null

	/**
	 * Icone de la ressource, généralement égal à l'icone du processing en état closed ou opened
	 * mais pourrait retourner une icone surchargée avec des états particuliers issus de JNodeShortProps.
	 */
	resIcon(res: JNodeShortProps, state?: 'opened' | 'closed' | string): string

	/** Nom de la res affiché dans les arbres et listes, généralement égal à JNodeShortProps.n */
	resName(res: JNodeShortProps): string

	/** Titre de la ressource */
	resTitle?(res: JNodeInfos): string

	/** Version de la res affiché dans les arbres et listes, généralement égal à JNodeShortProps.v */
	resVersion(res: JNodeShortProps): string

	/**
	 * Retourne le path "live" d'une ressource.
	 * Le path live est le path "stable" qui devrait en général être pris en compte pour juger si l'affichage d'une ressource
	 * doit être rafraichie suite à une modification.
	 * En VCB, le path live intègre la version du noeud terminal de la ressource (ou une partie d'entre elle).
	 * En VCS ou None, le path live est le resPath de la ressource sans la version de son noeud terminal.
	 */
	livePath(resPath: resPath): resPath

	/**
	 * Area pour construire une view dans un env complet IReg<IDepotResUiEnv>.
	 * - main : vue principale éditable
	 * - infosMain : liste des propriétés principales de la res, destinées à être affichées en 1er plan
	 * - infos : liste complète des propriétés de la ressource (métas, url d'accès, etc.)
	 * - select : vue affichée pour sélectionner une ressource.
	 * - consult : vue d'une res isolée sans navigation possible et en readOnly.
	 */
	resView(key: "main" | "infosMain" | "infos" | "select" | "consult" | "resps" | string): IArea<IRegPointer<IDepotResUiEnv>> | null

	/** Area pour construire une view dans un env partiel IReg<IDepotShortResUiEnv> éphémère. */
	shortResView(key: "preview" | "hover" | string): IArea<IRegPointer<IDepotShortResUiEnv>> | null

	/** Area pour construire un formulaire d'édition des metas. Doit créer un tag contenant des champs de formulaire, de type fieldSet. */
	resForm(key: "editMetas" | "editIdent" | "editResps" | string): IArea<IResFormCtx | IResFormIdentCtx, IField & HTMLElement> | null

	/** Ce ResType est-il compatible avec ce nom de fichier */
	matchResName(name: string): boolean

	/** Ce ResType est-il compatible avec ce mimetype */
	matchResMimeType(mimeType: string): boolean

	/**
	 * Construit un env partiel statique pour cette res (preview, tooltips...).
	 * ATTENTION : les extPoints du resType ne sont pas recopiées dans le reg retourné :
	 * a priori pas utile et constitue une optimasation car newShortResRegFromDepotReg() est très
	 * fréquemment appelé lors des switch de Ctx (ActionHackCtx).
	 */
	newShortResRegFromDepotReg<ENV extends IDepotEnv>(from: IReg<ENV>, res: JNodeShortProps): IReg<ENV & IDepotShortResEnv>

}


/** Env dédié au reg associé à un ResType. */
export interface IDepotResTypeEnv extends IDepotEnv {

	/** ResType associé. */
	resType?: IResType
}

/** Structure de donnée d'un dossier de classification des ResTypes. */
export interface IDirResType {
	label: string
	closed: boolean
	children: (IDirResType | IResType)[]
}

export function isDirResType(d: any): d is IDirResType {return d && Array.isArray(d.children)}

/** Utilitaire pout compter les IResType dans un arbre. */
export function countResTypes(inArray: (IDirResType | IResType)[], until: number = Infinity, filter?: (resType: IResType) => boolean): number {
	let found = 0;
	for (let entry of inArray) {
		if (isDirResType(entry)) {
			found += countResTypes(entry.children, until - found, filter);
			if (found >= until) return found;
		} else if (!filter || filter(entry)) {
			if (++found >= until) return found;
		}
	}
	return found;
}

export class ResType implements IResType {

	prc: string;

	prcLabel: string;

	prcIcon: string;

	prcIconOpened?: string;

	prcIsFolder: boolean = false;

	prcIsNoContent: boolean = false;

	prcNoCreator: boolean = false;

	prcVersionning?: 'VCS' | 'VCB' | null = null;

	prcExts?: string[];

	prcMimes?: string[];

	reg: IReg<IDepotResTypeEnv>;

	protected views: Map<string, IArea<IRegPointer<IDepotResUiEnv> | IResFormCtx | IRegPointer<IDepotShortResUiEnv>>> = new Map();

	constructor(reg: IReg<IDepotEnv>, prc: string) {
		this.reg = REG.createSubReg<IDepotResTypeEnv>(reg);
		this.reg.env.resType = this;
		this.prc = prc;
	}

	resIcon(res: JNodeShortProps, state?: 'opened' | 'closed' | string): string {
		if (res.n === "" /* attention res.permaPath pas toujours dispo (si ancêtre d'une res...)*/) {
			//todo props du ResType pour éliminer cette icone en dur ?
			return "/@skin@/store/objects/res/home.svg";
		}
		return state === 'opened' ? this.prcIconOpened || this.prcIcon : this.prcIcon
	}

	resName(res: JNodeShortProps): string {
		let n = res.n;
		if (n) return n; //res.v ? `${n} (${res.v})` : n;
		if (n === "") return this.reg.env.universe.getName(); //"Accueil";
		return "?";
		// n = URLTREE.extractLeafName(res.path);
		// if (Object.isExtensible(res)) (res as JNodeProps).n = n; //cache
		// return n;
	}

	resVersion(res: JNodeShortProps): string {return res.v || ""}

	livePath(resPath: resPath): resPath {
		if (this.prcVersionning === "VCB") return resPath;
		return URLTREE.extractUnversionedLeafPath(resPath);
	}

	//resTitle(res: JNodeShortProps): string | null {return null}

	resView(key: "main" | "infosMain" | "infos" | "select" | "consult" | string): IArea<IRegPointer<IDepotResUiEnv>> | null {
		const v = this.views.get(key);
		if (v) return v;
		if (key === "main") return RESVIEW_main;
		if (key === "infos") return RESVIEW_infos;
		if (key === "select") {
			const main = this.views.get("main") || RESVIEW_main;
			if ((main as IResAreaAdapter).asSelectArea) return (main as IResAreaAdapter).asSelectArea();
			return RESVIEW_main;
		}
		if (key === "consult") {
			const main = this.views.get("main") || RESVIEW_main;
			if ((main as IResAreaAdapter).asConsultArea) return (main as IResAreaAdapter).asConsultArea();
			return RESVIEW_main;
		}
		return null;
	}

	shortResView(key: "preview" | "hover" | string): IArea<IRegPointer<IDepotShortResUiEnv>> | null {
		const v = this.views.get(key);
		if (v) return v;
		if (key === "hover") {
			const preview = this.views.get("preview");
			if (preview) {
				const hover = new ResHoverArea().setPreview(preview);
				this.views.set(key, hover);
				return hover;
			}
			return RESVIEW_infos;
		}
		return null;
	}

	resForm(key: "editMetas" | "editIdent" | "editResps" | string): IArea<IResFormCtx | IResFormIdentCtx, IField & HTMLElement> | null {
		const v = this.views.get(key) as any;
		if (v) return v;
		if (key === "editIdent") return RESFORM_editIdent;
		return null;
	}

	matchResName(name: string): boolean | null {
		if (!name || !this.prcExts) return;
		const n = name.toLowerCase();
		return this.prcExts.find((ext) => n.endsWith(ext)) != null;
	}

	matchResMimeType(mimeType: string): boolean {
		if (!mimeType || !this.prcMimes) return;
		return FILES.isMimeTypeAllowed(mimeType, this.prcMimes);
	}


	/** Affectation d'une propriété / méthode quelconque. */
	override(name: keyof ResType, val: any): this {
		(this[name] as any) = val;
		return this;
	}

	/** icône (fermé) */
	setIcon(icon: string): this {
		this.prcIcon = this.reg.env.resolver.resolve(icon).url;
		return this;
	}

	/** icône (ouvert) */
	setIconOpened(icon: string): this {
		this.prcIconOpened = this.reg.env.resolver.resolve(icon).url;
		return this;
	}

	addView(key: "main" | "infosMain" | "infos" | "select" | "consult" | "resps" | string, view: IArea<IRegPointer<IDepotResUiEnv>>): this {
		this.views.set(key, view);
		return this;
	}

	addShortView(key: "hover" | string, view: IArea<IRegPointer<IDepotShortResUiEnv>>): this {
		this.views.set(key, view);
		return this;
	}

	addEditView(key: "editMetas" | "editIdent" | "editResps" | string, view: IArea<IResFormCtx>): this {
		this.views.set(key, view);
		return this;
	}

	newShortResRegFromDepotReg<ENV extends IDepotEnv>(from: IReg<ENV>, res: JNodeShortProps): IReg<ENV & IDepotShortResEnv> {
		const reg = REG.createSubReg(from) as IReg<ENV & IDepotShortResEnv>;
		reg.env.path = res.permaPath;
		reg.env.nodeInfos = res;
		reg.env.resType = this;
		reg.env.securityCtx = SEC.createSub(from.env.securityCtx, res.roles, undefined, res);
		return reg;
	}
}

/**
 * Contexte d'exécution d'un formulaire d'édition de métas.
 */
export interface IResFormCtx extends IRegPointer<IDepotResUiEnv>, IControlLabelContext {
}


/**
 * Contexte d'exécution d'un formulaire d'édition des metas identificatoires de la res.
 *
 * L'env IDepotResUiEnv du reg de ce contexte est partiel, seul le prc est fixé.
 *
 * @see DepotUniverse.newDepotResUiRegInCreation()
 */
export interface IResFormIdentCtx extends IResFormCtx, OResFormIdentConfig {


	/**
	 * Callback sur l'état des métas identificatoires de la res qui peut être appelée fréquement (retour UI dynamique)
	 * avant l'appel à checkValidity/reportValidity du formulaire.
	 *
	 * @param resState
	 *	invalid : les champs sont incorrects, impossible de de construire le path de la ressource
	 *	pending : traitement en cours pour connaitre l'état du path
	 *	exist : la ressource avec ce path existe
	 *	free : la ressource avec ce path pourrait être créée
	 *	inTrash : la ressource avec ce path existe mais est en état trashed
	 * @param targetPath renseigné lorsque resState!=='invalid'
	 * @param msg, msgLevel message affiché au user avec son level.
	 */
	callback?: { identState(resState: 'invalid' | 'pending' | 'exist' | 'free' | 'inTrash', targetPath?: resPath | null, msg?: string, msgLevel?: 'info' | 'error' | 'warning' | 'valid'): void }
}

export interface OResFormIdentConfig {
	/**
	 * Valeur par défaut du path du dossier parent.
	 */
	folderPath?: resPath

	/**
	 * Affichage des widgets de sélection du path du dossier parent.
	 * editable : le dossier d'insertion peut être modifié
	 * readOnly : le dossier ne peut être modifié mais est affiché (space doit être renseigné)
	 * hidden : le dossier n'est pas affiché  (folderPath doit être renseigné)
	 */
	folderUi?: 'editable' | 'readOnly' | 'hidden'

	/** Valeur par défaut du nom terminal de la res (avec son extension), sans la version. */
	leafName?: string

	/** MimeType de la res */
	mimeType?: string

	/**
	 * Affichage des widgets de saisie du nom terminal de la ressource (sans sa version).
	 * editable : le nom de la res est modifiable
	 * readOnly : le nom de la res est affiché mais non éditable
	 * hidden : le nom de la res est masqué
	 */
	leafUi?: 'editable' | 'readOnly' | 'hidden'

	/** Version par défaut de la res. */
	version?: string

	/**
	 * Affichage des widgets de saisie de la version de la ressource.
	 * editable : la version de la res est modifiable
	 * readOnly : la version de la res est affichée mais non éditable
	 * hidden : la version de la res est masquée
	 */
	versionUi?: 'editable' | 'readOnly' | 'hidden'

	/** Si une res avec le path cible existe déjà dans le dépot, autorise-t-on sa modif. */
	resUpdatable?: 'always' | 'ifInTrash' | 'never'
}

const RESVIEW_main = new ResMainArea().setContent(null);
const RESVIEW_infosMain_MOVED = new ResInfoArea().setLines(RESINFO.moved);
const RESVIEW_main_FOLDER = new ResMainArea().setContent(new ResTabsArea().setTabs(
		new ResChildrenArea().setLabel("Gestion"),
		new ResFrameArea().setLabel("Rendu")
	)
);
const RESVIEW_infos = new ResInfoArea().setLines(RESINFO.path, RESINFO.url, RESINFO.status, RESINFO.update, RESINFO.prc); //, RESINFO.debug , RESINFO.moved: non car besoin de fermer le tooltip en cours à impl.
const RESFORM_editIdent = new ResIdentFieldSetArea();

export namespace RES {
	export const NODEPROPS_short = "n*permaPath*v*idx*m*t*trashed*unlisted*prc*resId*roles";
}